import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { disableAnalytics, enableAnalytics } from '../lib/analytics';
import { LEGAL_VERSIONS } from '../config/site';

const KEY = 'pa_cookie_consent';
// CNIL guidance: keep a cookie choice for about six months, then ask again.
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 182;
const ConsentContext = createContext(null);

function read() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    const fresh = v && Date.now() - new Date(v.decidedAt).getTime() < MAX_AGE_MS;
    if (v && v.version === LEGAL_VERSIONS.cookies && fresh) return v;
  } catch (e) { /* storage unavailable */ }
  return null;
}

export function ConsentProvider({ children }) {
  const [choice, setChoice] = useState(() => read());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (choice?.analytics) enableAnalytics();
    else disableAnalytics();
  }, [choice]);

  const save = useCallback((prefs) => {
    const value = { analytics: !!prefs.analytics, decidedAt: new Date().toISOString(), version: LEGAL_VERSIONS.cookies, source: prefs.source || 'banner' };
    try { localStorage.setItem(KEY, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
    setChoice(value);
    setSettingsOpen(false);
  }, []);

  // Global Privacy Control is treated as a refusal, without asking.
  useEffect(() => {
    if (!choice && typeof navigator !== 'undefined' && navigator.globalPrivacyControl) {
      save({ analytics: false, source: 'gpc' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    choice,
    decided: !!choice,
    analyticsAllowed: !!choice?.analytics,
    acceptAll: () => save({ analytics: true }),
    rejectAll: () => save({ analytics: false }),
    save,
    settingsOpen,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
  }), [choice, save, settingsOpen]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used inside ConsentProvider');
  return ctx;
}
